import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Modal, Form, Button, Spinner } from "react-bootstrap";
import { Check } from "react-bootstrap-icons";
import Compressor from "compressorjs";
import { 
  validatePDFBase64, 
  sanitizeBase64, 
  logValidationReport 
} from "../utils/pdfValidator";

/**
 * ImageModal
 * - Shows a button that opens a modal to preview/upload one receipt (image or PDF)
 * - Accepts initial file via `info` { fileName, base64Container, ProductType }
 * - Calls `onUpload(pictures)` on Save where pictures = [{ src, fileName, kind, b64? }]
 *
 * Best-practices applied:
 * 1) Concrete viewer height so images always paint (no 0-height + repaint-on-scroll issues).
 * 2) Never hide <img> with display:none. Spinner overlays instead.
 * 3) Use objectFit: 'contain' to show full image; remove fixed width/height constraints.
 * 4) Force remount with a stable key when the src changes.
 * 5) Revoke created object URLs on cleanup to avoid memory leaks.
 * 6) Defensive loading logic (short failsafe timeout to avoid stuck spinner with cached/data URLs).
 */

const VIEWPORT_MAX_VH = 70; // max height inside modal viewport
const MAX_FILE_SIZE_BYTES = 30 * 1024 * 1024; // 30MB

function dataUrlFromBase64({ base64Container, fileName }) {
  const ext = (fileName || "").split(".").pop()?.toLowerCase();
  if (ext === "pdf") {
    return `data:application/pdf;base64,${base64Container}`;
  }
  const type = ["jpg", "jpeg", "png", "gif"].includes(ext || "")
    ? (ext === "jpg" ? "jpeg" : ext)
    : "jpeg";
  return `data:image/${type};base64,${base64Container}`;
}

const ImageModal = ({ info = {}, onUpload }) => {
  const [show, setShow] = useState(false);
  const [isVisible, setIsVisible] = useState(false); // true only after Modal fully enters
  const [pictures, setPictures] = useState([]); // [{ src, fileName, kind: 'image'|'pdf', b64? }]
  const [loading, setLoading] = useState({}); // { [fileName]: boolean }
  const objectUrls = useRef(new Map()); // fileName => objectURL (for revocation)
  const modalKey = useRef(Date.now());

  const resetViewerLoading = useCallback((files) => {
    const next = {};
    files.forEach((f) => (next[f.fileName] = true));
    setLoading(next);

    // Fail-safe: ensure spinner cannot mask the content if onLoad doesn't fire
    const timer = setTimeout(() => {
      setLoading((prev) => {
        const cleared = { ...prev };
        Object.keys(cleared).forEach((k) => (cleared[k] = false));
        return cleared;
      });
    }, 900);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = useCallback(() => setShow(false), []);
  const handleShow = useCallback(() => {
    modalKey.current = Date.now();
    setShow(true);
  }, []);

  // Initialize from `info`
  useEffect(() => {
    if (!info) return;
    const hasInitial = Boolean(info?.fileName?.length || info?.base64Container);
    if (!hasInitial) return;

    const fileName = info.fileName || "image.jpeg";
    const ext = (fileName || "").split(".").pop()?.toLowerCase();
    const kind = ext === "pdf" ? "pdf" : "image";
    const src = dataUrlFromBase64({ base64Container: info.base64Container, fileName });

    const initial = [{ src, fileName, kind, b64: info.base64Container }];
    setPictures(initial);

    const cleanup = resetViewerLoading(initial);
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info?.fileName, info?.base64Container]);

  // Cleanup all object URLs on unmount
  useEffect(() => {
    return () => {
      objectUrls.current.forEach((url) => {
        try { URL.revokeObjectURL(url); } catch {}
      });
      objectUrls.current.clear();
    };
  }, []);

  const removePicture = useCallback((name) => {
    setPictures((prev) => prev.filter((p) => p.fileName !== name));
    // Revoke any object URL tied to this fileName
    const url = objectUrls.current.get(name);
    if (url) {
      try { URL.revokeObjectURL(url); } catch {}
      objectUrls.current.delete(name);
    }
    setLoading((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const markLoaded = useCallback((name) => {
    setLoading((prev) => ({ ...prev, [name]: false }));
  }, []);

  const handleSubmit = useCallback(() => {
    if (onUpload) onUpload(pictures);
  }, [onUpload, pictures]);

  // --- File handling / compression ---
  const makePictureFromFile = useCallback((fileObj) => {
    return new Promise((resolve, reject) => {
      const fileName = fileObj.name;
      const ext = (fileName || "").split(".").pop()?.toLowerCase();
      const kind = ext === "pdf" ? "pdf" : "image";

      if (kind === "pdf") {
        const reader = new FileReader();
        reader.readAsDataURL(fileObj);
        reader.onload = async () => {
          try {
            const dataUrl = reader.result;
            let b64 = String(dataUrl).replace("data:application/pdf;base64,", "");
            
            // Step 1: Sanitize Base64 (remove whitespace)
            const sanitized = sanitizeBase64(b64);
            if (!sanitized.valid) {
              console.warn(`PDF sanitization warning for ${fileName}: ${sanitized.error}`);
              // Don't reject - still allow, but warn
            } else {
              b64 = sanitized.sanitized;
            }
            
            // Step 2: Validate PDF structure
            const validation = await validatePDFBase64(b64);
            
            // Log validation results for debugging
            logValidationReport(fileName, b64, validation);
            
            if (!validation.valid) {
              // For user feedback, show error but allow file anyway (user discretion)
              console.error(`PDF validation failed: ${validation.error}`, validation.details);
              alert(`⚠️ Warning: PDF "${fileName}" may be corrupted:\n${validation.error}\n\nYou can still upload, but merge may fail.`);
            }
            
            if (validation.warning) {
              console.warn(`PDF size warning: ${validation.warning}`);
            }
            
            const objUrl = URL.createObjectURL(fileObj);
            objectUrls.current.set(fileName, objUrl);
            resolve({ src: objUrl, fileName, kind: "pdf", b64 });
          } catch (err) {
            console.error('PDF processing error:', err);
            reject(err);
          }
        };
        reader.onerror = reject;
        return;
      }

      // Images: compress non-JPEG, pass-through (or recompress) JPEG
      const handleCompressed = (blob) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onload = () => {
          const dataUrl = reader.result;
          const base64Data = String(dataUrl).replace(/^data:[^;]+;base64,/, "");
          const objUrl = URL.createObjectURL(blob);
          objectUrls.current.set(fileName, objUrl);
          resolve({ src: objUrl, fileName, kind: "image", b64: base64Data });
        };
        reader.onerror = reject;
      };

      const compress = () => new Compressor(fileObj, {
        quality: 0.6,
        success: handleCompressed,
        error: reject,
      });

      if (ext === "jpg" || ext === "jpeg") {
        compress();
      } else {
        compress();
      }
    });
  }, []);

  const handleInputChange = useCallback(async (event) => {
    const { files } = event.target;
    if (!files || !files.length) return;

    const fileObj = files[0];
    if (fileObj.size > MAX_FILE_SIZE_BYTES) {
      alert(`"${fileObj.name}" is ${(fileObj.size / (1024 * 1024)).toFixed(1)}MB. Max attachment size is 30MB.`);
      event.target.value = "";
      return;
    }

    try {
      const pic = await makePictureFromFile(fileObj);
      setPictures([pic]);
      const cleanup = resetViewerLoading([pic]);
      return cleanup;
    } catch (err) {
      console.error("File load error:", err);
    }
  }, [makePictureFromFile, resetViewerLoading]);

  // Viewer wrapper styles (shared for image & PDF)
  const viewerStyle = useMemo(() => ({
    width: "100%",
    height: `min(${VIEWPORT_MAX_VH}vh, 600px)`, // concrete height ensures consistent paint
    position: "relative",
    overflow: "auto",
    display: "block", // avoid flex paint quirks
    background: "#f8f9fa",
    borderRadius: 8,
    border: "1px solid rgba(0,0,0,.05)",
  }), []);

  return (
    <>

      <button
        className="link row__addImage"
        aria-label="Add Receipt"
        title="Add Receipt"
        onClick={handleShow}
      >
        {pictures.length >= 1 ? (
          <span className="iconAddItem icon-check bg-primary">
            <Check color="white" size={20} />
          </span>
        ) : (
          <span className="iconAddItem icon-add bg-success  "></span>

        )}
      </button>

      <Modal
        key={modalKey.current}
        show={show}
        onHide={handleClose}
        animation={false}
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Receipt Upload</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          This Receipt is for <strong>{info?.ProductType}</strong>

          {pictures.length < 1 && (
            <Form className="mt-3">
              <Form.Group controlId="formFileSingle" className="mb-3">
                <Form.Control
                  name="files"
                  type="file"
                  accept="image/gif, image/jpeg, image/png, application/pdf"
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Form>
          )}

          {pictures.length > 0 && pictures.map((file) => (
            <div key={file.fileName} className="my-2">
              <div style={{ position: "relative" }}>
                <button
                  name={file.fileName}
                  className="link picDelete"
                  aria-label="Remove File"
                  title="Remove File"
                  style={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    zIndex: 3,
                    background: "transparent",
                    border: "none",
                  }}
                  onClick={() => removePicture(file.fileName)}
                >
                  <span className="icon icon-remove bg-red"></span>
                </button>

                <div style={viewerStyle}>
                  {loading[file.fileName] && (
                    <Spinner animation="border" variant="primary" style={{ position: "absolute", zIndex: 2 }} />
                  )}

                  {file.kind === "pdf" ? (
                    <iframe
                      key={`${modalKey.current}-${file.src}`}
                      src={file.src}
                      title={file.fileName}
                      width="100%"
                      height="100%"
                      style={{ border: 0, background: "#fff" }}
                      onLoad={() => markLoaded(file.fileName)}
                    />
                  ) : (
                    <img
                    className="img-nudge mx-auto"
                      key={`${modalKey.current}-${file.src}`}
                      src={file.src}
                      alt="Uploaded receipt"
                      loading="eager"
                      decoding="sync"
                      style={{
                        display: "block",
                        maxWidth: "300px",
                        maxHeight: "350px",
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                        background: "#fff",
                      }}
                      onLoad={() => markLoaded(file.fileName)}
                      onError={() => markLoaded(file.fileName)}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={() => {
              handleClose();
              handleSubmit();
            }}
            disabled={pictures.length < 1}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ImageModal;
